// Keeps a prerendered page on screen only until React's first commit.
//
// createRoot() replaces #root's children on its first commit. The route
// components are lazy, so that first commit is usually the Suspense fallback:
// Moving the prerendered nodes into a sibling avoids an empty mount frame.
// Release that static copy at the first commit even if a lazy route is still
// loading: its buttons have no React handlers and must not look ready for taps.
// The existing loading fallback is honest about readiness; no clicks are replayed.

const SAFETY_TIMEOUT_MS = 15000;
const HIDDEN_ROOT_STYLES = ['position', 'top', 'left', 'right', 'visibility', 'pointer-events'];

let snapshot = null;
let rootElement = null;
let pendingFallbacks = 0;
let safetyTimer = null;

export const releasePrerenderedPage = () => {
  if (!snapshot) return;
  window.clearTimeout(safetyTimer);
  snapshot.remove();
  snapshot = null;
  safetyTimer = null;
  pendingFallbacks = 0;
  HIDDEN_ROOT_STYLES.forEach((name) => rootElement.style.removeProperty(name));
  rootElement.removeAttribute('aria-hidden');
};

export const holdPrerenderedPage = (root) => {
  if (!root || !root.firstElementChild) return;
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;

  rootElement = root;
  snapshot = document.createElement('div');
  snapshot.setAttribute('data-mediasfu-prerender-snapshot', '');
  while (root.firstChild) snapshot.appendChild(root.firstChild);
  root.parentNode.insertBefore(snapshot, root);

  // Out of flow but still laid out, so mount-time measurements stay real.
  Object.assign(root.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  root.setAttribute('aria-hidden', 'true');
  safetyTimer = window.setTimeout(releasePrerenderedPage, SAFETY_TIMEOUT_MS);
};

// Called from the route Suspense fallback's layout effect. Returns the cleanup.
export const trackRouteFallback = () => {
  if (!snapshot) return undefined;
  const heldSnapshot = snapshot;
  pendingFallbacks += 1;
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    if (snapshot !== heldSnapshot) return;
    pendingFallbacks = Math.max(0, pendingFallbacks - 1);
    // If a fallback resolves before AppBootstrap's layout effect, release at
    // the end of the commit. Stale cleanup never touches a later snapshot.
    queueMicrotask(() => {
      if (snapshot === heldSnapshot && pendingFallbacks <= 0) releasePrerenderedPage();
    });
  };
};

// Called once the app's first commit has run its layout effects. The route may
// still show its loading fallback; never leave static, inert controls above it.
export const noteAppCommitted = () => {
  releasePrerenderedPage();
};
